import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, X } from 'lucide-react';
import { spotlightMaskStyle } from './spotlightMask';
import { placeCard } from './cardPlacement';
import { revealTourTarget } from '../data/tourRegistry';
import { useAuth } from '../../Accounts/AuthContext';
import { loadUiFlags, setUiFlag } from '../../pages/helpers/uiFlags';

/*
 * PageTour — the interactive walkthrough (Sandy 2026-07-14). Blurs and dims
 * everything except the element being explained, one step at a time. Runs
 * automatically the FIRST time a user opens a page (per-page localStorage
 * key), and can be redone anytime from the floating ? button. Shared by the
 * student and teacher dashboards; each page passes its own steps.
 *
 * Steps: [{ target: '<data-tour value>' | null, title, body }]
 * target null = centered card over a fully dimmed page (intro/outro steps).
 * Target elements are found by [data-tour="..."] at open time, so pages just
 * sprinkle attributes; nothing re-renders for the tour.
 *
 * Future: the AI help chat can invoke these steps to SHOW a user an answer
 * ("where do I change the plan?") instead of describing it.
 */

const PAD = 8;          // spotlight padding around the target
const CARD_W = 330;

export default function PageTour({ pageKey, steps, showLauncher = false }) {
  const storageKey = 'bb.tour.' + pageKey;
  const auth = useAuth();
  const [idx, setIdx] = useState(null); // null = closed
  const [rect, setRect] = useState(null);
  const cardRef = useRef(null);
  const [cardH, setCardH] = useState(0);

  // First visit: run automatically (slight delay so the page settles). "First"
  // is per USER, not per browser (Sandy 2026-08-07): the server-side uiFlags
  // map is checked alongside localStorage, so a tour finished on one device
  // never re-runs on another. If Sandy (the AI assistant) is pointing at
  // something on this page, he has the stage: skip the auto-run this visit,
  // it stays pending for next time.
  useEffect(() => {
    // Temporarily keep teacher walkthroughs manual-only, including first login.
    if (pageKey.startsWith('teacher-')) return undefined;
    // Admin view mode: no auto-run and no uiFlags reads (they would read the
    // admin's own flags).
    if (auth?.viewAs) return undefined;
    let t;
    let alive = true;
    try {
      if (localStorage.getItem(storageKey)) return undefined;
    } catch { return undefined; /* storage unavailable: tours become manual-only */ }
    loadUiFlags(auth).then((flags) => {
      if (!alive) return;
      if (flags && flags['tour.' + pageKey]) {
        // Seen elsewhere: backfill the local mirror and stay closed.
        try { localStorage.setItem(storageKey, 'done'); } catch { /* fine */ }
        return;
      }
      t = setTimeout(() => {
        if (window.__sandyTourPending || window.bbTour?.isOpen?.()) return;
        setIdx(0);
      }, 800);
    });
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, auth?.user]);

  const step = idx != null ? steps[idx] : null;

  // Real card height, measured before paint so placement never trusts a guess.
  useLayoutEffect(() => {
    if (step && cardRef.current) setCardH(cardRef.current.offsetHeight);
  }, [step, rect]);

  // Find + measure the target; re-measure on resize/scroll.
  useEffect(() => {
    if (!step) { setRect(null); return undefined; }
    const find = () => (step.target ? document.querySelector(`[data-tour="${step.target}"]`) : null);
    // A target in a hidden view: open its tab, then find it on the next frame.
    if (!find()) revealTourTarget(step.target);
    let scrolled = false;
    const measure = () => {
      const el = find();
      if (!el) { setRect(null); return; }
      if (!scrolled) { scrolled = true; el.scrollIntoView({ block: 'center', behavior: 'auto' }); }
      const r = el.getBoundingClientRect();
      setRect({ x: r.left - PAD, y: r.top - PAD, w: r.width + PAD * 2, h: r.height + PAD * 2 });
    };
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [step]);

  const finish = () => {
    // A tour opened in view mode marks nothing done, for anyone.
    if (!auth?.viewAs) {
      try { localStorage.setItem(storageKey, 'done'); } catch { /* fine */ }
      setUiFlag(auth, 'tour.' + pageKey); // per-user: never again on any device
    }
    setIdx(null);
  };
  const next = () => (idx >= steps.length - 1 ? finish() : setIdx(idx + 1));
  const back = () => idx > 0 && setIdx(idx - 1);

  // Keyboard: Esc closes, arrows move.
  useEffect(() => {
    if (idx == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') finish();
      if (e.key === 'ArrowRight' || e.key === 'Enter') next();
      if (e.key === 'ArrowLeft') back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  // Visuals: one full-viewport dim/blur layer with a rounded hole masked out
  // (spotlightMask.js). Clicks: four invisible panels around the hole, so the
  // spotlit element itself stays interactive exactly as before.
  const panelStyle = { position: 'fixed' };
  const panels = rect
    ? [
        { ...panelStyle, left: 0, top: 0, width: '100vw', height: Math.max(0, rect.y) },
        { ...panelStyle, left: 0, top: rect.y, width: Math.max(0, rect.x), height: rect.h },
        { ...panelStyle, left: rect.x + rect.w, top: rect.y, width: `calc(100vw - ${rect.x + rect.w}px)`, height: rect.h },
        { ...panelStyle, left: 0, top: rect.y + rect.h, width: '100vw', height: `calc(100vh - ${rect.y + rect.h}px)` },
      ]
    : [{ ...panelStyle, left: 0, top: 0, width: '100vw', height: '100vh' }];

  // Card placement: below the spotlight if the measured card fits, above if
  // that fits, else clamped into the viewport. No target = centered.
  const cardStyle = rect
    ? placeCard(rect, CARD_W, cardH)
    : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };

  return (
    <>
      {/* redo button: always available, bottom-right (pages can hide it) */}
      {idx == null && showLauncher && (
        <button
          type="button"
          onClick={() => setIdx(0)}
          title="Show me around this page"
          className="fixed bottom-5 right-5 w-10 h-10 rounded-full bg-white border border-neutral-300 shadow-[0_4px_12px_rgba(0,0,0,.12)] flex items-center justify-center cursor-pointer text-neutral-500 hover:text-ink hover:border-neutral-400"
          style={{ zIndex: 9000 }}
        >
          <HelpCircle size={20} strokeWidth={2.25} />
        </button>
      )}

      {step && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000 }}>
          <div
            style={{
              position: 'fixed', inset: 0, pointerEvents: 'none',
              background: 'rgba(20,20,20,0.45)',
              backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
              ...spotlightMaskStyle(rect),
            }}
          />
          {panels.map((s, i) => (
            <div key={i} style={s} onClick={next} />
          ))}

          {/* spotlight glow: soft gradient, no hard ring (Sandy 2026-07-21) */}
          {rect && (
            <div
              className="rounded-[14px] pointer-events-none"
              style={{
                position: 'fixed', left: rect.x, top: rect.y, width: rect.w, height: rect.h,
                boxShadow: '0 0 6px 1px rgba(255,255,255,0.7), 0 0 16px 5px rgba(147,197,253,0.5), 0 0 34px 14px rgba(59,130,246,0.22)',
              }}
            />
          )}

          {/* step card */}
          <div
            ref={cardRef}
            className="fixed bg-white rounded-[12px] border border-neutral-200 shadow-[0_24px_60px_rgba(0,0,0,.25)] p-4"
            style={{ width: CARD_W, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', ...cardStyle }}
          >
            <div className="flex items-start gap-2">
              <div className="text-[15px] font-extrabold text-ink leading-snug flex-1">{step.title}</div>
              <button
                type="button"
                onClick={finish}
                title="Skip the tour"
                className="border-none bg-transparent p-0.5 cursor-pointer text-neutral-400 hover:text-ink"
              >
                <X size={15} strokeWidth={2.5} />
              </button>
            </div>
            <p className="mt-1.5 mb-3 text-[13px] text-neutral-600 leading-relaxed">{step.body}</p>
            <div className="flex items-center gap-1.5">
              {steps.map((_, i) => (
                <span key={i} className={'w-1.5 h-1.5 rounded-full ' + (i === idx ? 'bg-ink' : 'bg-neutral-200')} />
              ))}
              <span className="flex-1" />
              {idx > 0 && (
                <button
                  type="button"
                  onClick={back}
                  className="cursor-pointer text-xs font-bold px-3 py-1.5 rounded-[6px] text-neutral-600 bg-white border border-neutral-300 hover:bg-neutral-50"
                >
                  Back
                </button>
              )}
              <button
                type="button"
                onClick={next}
                className="cursor-pointer text-xs font-bold px-3.5 py-1.5 rounded-[6px] text-white bg-primary-500 hover:bg-primary-600 border border-primary-600"
              >
                {idx >= steps.length - 1 ? 'Done' : 'Next'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
